// Capture a poster frame from a local video File in the browser, while it uploads.
// Gives the dashboard card an instant thumbnail during processing; the worker's
// ffmpeg poster (same filename) replaces it once transcription completes.
// Resolves null on any failure (unsupported codec, black metadata, timeout) —
// callers treat the poster as strictly optional.
export function captureVideoPoster(file, { maxDim = 1280, quality = 0.82, timeoutMs = 8000 } = {}) {
  return new Promise((resolve) => {
    let url = null;
    let video = null;
    let timer = null;
    let done = false;

    const finish = (result) => {
      if (done) return;
      done = true;
      if (timer) clearTimeout(timer);
      if (video) {
        video.onerror = null;
        video.onloadedmetadata = null;
        video.onseeked = null;
        video.removeAttribute('src');
      }
      if (url) URL.revokeObjectURL(url);
      resolve(result);
    };

    timer = setTimeout(() => finish(null), timeoutMs);

    try {
      url = URL.createObjectURL(file);
      video = document.createElement('video');
      video.muted = true;
      video.playsInline = true;
      video.preload = 'metadata';

      video.onerror = () => finish(null);
      video.onloadedmetadata = () => {
        // Seek a little past 0 to skip a black lead-in frame, but stay near the
        // start so only the head of the file needs decoding. Never seek to
        // exactly 0 — some browsers skip the 'seeked' event for a no-op seek.
        const d = video.duration;
        const t = Number.isFinite(d) && d > 0 ? Math.min(1, d * 0.1) : 0.01;
        video.currentTime = Math.max(t, 0.01);
      };
      video.onseeked = () => {
        try {
          const w = video.videoWidth;
          const h = video.videoHeight;
          if (!w || !h) return finish(null);
          const scale = Math.min(1, maxDim / Math.max(w, h));
          const canvas = document.createElement('canvas');
          canvas.width = Math.round(w * scale);
          canvas.height = Math.round(h * scale);
          canvas.getContext('2d').drawImage(video, 0, 0, canvas.width, canvas.height);
          canvas.toBlob(
            // width/height are the video's NATIVE dimensions — the card uses them
            // for aspect ratio, matching what the server's ffprobe would report.
            (blob) => finish(blob ? { blob, width: w, height: h } : null),
            'image/jpeg',
            quality
          );
        } catch {
          finish(null);
        }
      };

      video.src = url;
    } catch {
      finish(null);
    }
  });
}
